import React from 'react';

/**
 * What the Prospects views share (backend: Backend/admin_prospects.py,
 * section "leads": prospects, prospect_detail, prospect_settings,
 * prospect_settings_set, prospects_followup).
 */

export const LEADS = 'leads';

export type Band = 'hot' | 'warm' | 'cool';
export type Segment = 'explorer' | 'trial_active' | 'trial_lapsed';

export type Suggestion = { key: string; label: string; why: string; template_key: string | null };

export type Signals = Record<string, number | boolean | null>;

export type ProspectRow = {
  rank: number;
  user_id: string;
  email: string;
  name: string;
  organisation: string;
  segment: Segment;
  segment_label: string;
  signed_in: boolean;
  is_team: boolean;
  score: number;
  band: Band;
  activity_percentile: number | null;
  trial_started_at: string | null;
  trial_ended_at: string | null;
  trial_origin: string | null;
  last_active_at: string | null;
  last_checkout_at: string | null;
  last_blocked_at: string | null;
  cancel_reasons: string[];
  cancel_other: string;
  signals: Signals;
  reasons: string[];
  suggestion: Suggestion;
};

export type Component = { key: string; label: string; value: number; weight: number; cap: number; points: number };

export type ScoringSettings = {
  window_days: number;
  weights: Record<string, { weight: number; cap: number }>;
  bands: { hot: number; warm: number };
  thresholds: Record<string, number>;
  nudges: Record<string, { label: string; why: string; template_key: string | null }>;
  components: { key: string; label: string; help: string; default_weight: number; default_cap: number }[];
  nudge_order: { key: string; default_label: string; default_template_key: string | null }[];
};

export const SEGMENT_OPTIONS: { key: 'all' | Segment; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'explorer', label: 'Explorer' },
  { key: 'trial_active', label: 'Trial active' },
  { key: 'trial_lapsed', label: 'Trial lapsed' },
];

export const WINDOWS = ['7', '14', '30', '60', '90', '180'];

const BAND_LOOK: Record<Band, string> = {
  hot: 'bg-red-100 text-red-700 border-red-200',
  warm: 'bg-amber-100 text-amber-800 border-amber-200',
  cool: 'bg-grayscale-200 text-grayscale-1200 border-grayscale-300',
};
const BAND_BAR: Record<Band, string> = { hot: 'bg-red-500', warm: 'bg-amber-500', cool: 'bg-grayscale-500' };

/** The score as "72%" with a band-coloured bar: the owner likes plain % scores. */
export const ScorePill: React.FC<{ score: number; band: Band }> = ({ score, band }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={`${band} — score ${score} of 100`}>
    <span className={`text-xs font-bold px-1.5 py-0.5 rounded border ${BAND_LOOK[band]}`}>{score}%</span>
    <span className="w-12 h-1.5 rounded bg-grayscale-200 overflow-hidden">
      <span className={`block h-full ${BAND_BAR[band]}`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
    </span>
  </span>
);

const SEG_LOOK: Record<Segment, string> = {
  explorer: 'bg-primary-100 text-primary-og-600',
  trial_active: 'bg-green-100 text-green-800',
  trial_lapsed: 'bg-purple-100 text-purple-800',
};

export const SegmentPill: React.FC<{ segment: Segment; label?: string }> = ({ segment, label }) => (
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${SEG_LOOK[segment] || ''}`}>
    {label || segment}
  </span>
);

/** "3 days ago" — '' for no date. */
export const ago = (v: any): string => {
  if (!v) return '';
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const units: [number, string][] = [[365 * 86400, 'year'], [30 * 86400, 'month'], [7 * 86400, 'week'],
    [86400, 'day'], [3600, 'hour'], [60, 'minute']];
  for (const [n, u] of units) {
    if (s >= n) {
      const k = Math.floor(s / n);
      return `${k} ${u}${k === 1 ? '' : 's'} ago`;
    }
  }
  return '';
};

/** Signal keys → readable names, for the optional table columns and the drawer. */
export const SIGNAL_LABELS: Record<string, string> = {
  searches: 'Searches (lifetime)', searches_w: 'Searches (window)', search_days_w: 'Search days (window)',
  attempts: 'Search attempts (lifetime)', attempts_w: 'Search attempts (window)',
  chats: 'AI chats (lifetime)', chats_w: 'AI chats (window)', saved: 'Saved',
  logins: 'Sign-ins (lifetime)', login_days: 'Sign-in days (lifetime)', logins_w: 'Sign-ins (window)',
  active_days_w: 'Active days (window)', paywall: 'Paywall hits (lifetime)', paywall_w: 'Paywall hits (window)',
  upgrade_clicks: 'Upgrade clicks (lifetime)', upgrade_clicks_w: 'Upgrade clicks (window)',
  trial_intent: 'Trial intent (lifetime)', trial_intent_w: 'Trial intent (window)',
  checkout_opens: 'Checkout opens (lifetime)', checkout_opens_w: 'Checkout opens (window)',
  checkout_abandoned_n: 'Abandoned checkouts', checkout_abandoned: 'Checkout abandoned',
  contact_opens: 'Contact opens', contact_blocked: 'Contact blocked (lifetime)',
  contact_blocked_w: 'Contact blocked (window)', limit_requests: 'Limit requests (lifetime)',
  limit_requests_w: 'Limit requests (window)', concierge: 'Concierge (lifetime)', concierge_recent: 'Concierge (recent)',
  ip_intel: 'IP Intel (lifetime)', ip_intel_recent: 'IP Intel (recent)', email_opens_w: 'E-mail opens (window)',
  email_clicks_w: 'E-mail clicks (window)', profile_pct: 'Profile %', trial_used: 'Trial used',
  trial_activity: 'Activity since trial start', active_days_since_trial: 'Active days since trial start',
  searches_since_trial: 'Searches since trial start', days_since_lapse: 'Days since lapse',
};

export const fmtSignal = (v: any): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
};
